import { useState, useEffect } from "react";
import "./AddProducts.scss";
import {
  projectFirestore,
  projectStorage,
  timestamp,
} from "../firebase-config/config";
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";
import { useCollection } from "../firebase-files/hooks/useCollection";
import { useDocument } from "../firebase-files/hooks/useDocument";
import Select from "react-select";

const AddProducts = ({ isModalOpen, closeModal }) => {
  const { user } = useAuthContext();

  // snack bar functionality
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Fetch products category and sub category
  const { documents: productsCategories } = useCollection("m_categories");
  const { documents: vendors } = useCollection("vendors");
  const { document: multiLocationDoc } = useDocument(
    "settings",
    "multiLocation"
  );

  const [isLoading, setIsLoading] = useState(false);

  // add new product functionality
  const [newProduct, setNewProduct] = useState({
    name: "",
    brand: "",
    model: "",
    shortDescription: "",
    price: "",
    category: [],
    subCategory: [],
    images: [],
    buyerType: "",
    showProductTo: [],
    CustomProductUrl: "",
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [isMultiLocationEnabled, setIsMultiLocationEnabled] = useState(true);
  const [isSubscription, setIsSubscription] = useState(false);
  const [isProductType, setProductType] = useState("general");

  useEffect(() => {
    if (multiLocationDoc) {
      setIsMultiLocationEnabled(multiLocationDoc.isEnabled);
    }
  }, [multiLocationDoc]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setImageFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        newFiles[index] = file;
        return newFiles;
      });
    }
  };

  const handleRemoveImage = (index) => {
    setImageFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleBuyerTypeChange = (e) => {
    setNewProduct((prev) => ({ ...prev, buyerType: e.target.value }));
  };

  const handleVendorSelection = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setNewProduct((prev) => ({ ...prev, showProductTo: [] }));
      return;
    }

    if (selectedOptions.some((option) => option.value === "all")) {
      setNewProduct((prev) => ({ ...prev, showProductTo: ["all"] }));
    } else {
      setNewProduct((prev) => ({
        ...prev,
        showProductTo: selectedOptions.map((option) => option.value),
      }));
    }
  };

  const getVendorOptions = () => {
    const allOption = { value: "all", label: "All Vendors" };
    const vendorOptions =
      vendors?.map((vendor) => ({
        value: vendor.id,
        label: vendor.name,
      })) || [];

    return [allOption, ...vendorOptions];
  };

  const getCategoryOptions = () => {
    return (
      productsCategories
        ?.filter((cat) => cat.status === "active")
        .map((cat) => ({
          value: cat.category,
          label: cat.category,
        })) || []
    );
  };

  const getSubCategoryOptions = () => {
    const selectedCategories = Array.isArray(newProduct.category)
      ? newProduct.category
      : [newProduct.category];
    let subCategoryOptions = [];

    selectedCategories.forEach((categoryValue) => {
      const categoryDoc = productsCategories?.find(
        (cat) => cat.category === categoryValue
      );
      if (categoryDoc?.subCategories) {
        const subCats = categoryDoc.subCategories
          .filter((subCat) => subCat.status === "active")
          .map((subCat) => ({
            value: subCat.subCategoryName,
            label: subCat.subCategoryName,
            category: categoryValue,
          }));
        subCategoryOptions = [...subCategoryOptions, ...subCats];
      }
    });

    return subCategoryOptions;
  };

  const handleCategorySelection = (selectedOptions) => {
    const selectedCategories = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setNewProduct((prev) => ({
      ...prev,
      category: selectedCategories,
      subCategory: prev.subCategory.filter((subCat) => {
        const subCatOption = getSubCategoryOptions().find(
          (opt) => opt.value === subCat
        );
        return (
          subCatOption && selectedCategories.includes(subCatOption.category)
        );
      }),
    }));
  };

  const handleSubCategorySelection = (selectedOptions) => {
    setNewProduct((prev) => ({
      ...prev,
      subCategory: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  const handleAddProductNew = async (e) => {
    e.preventDefault();
    if (imageFiles.length === 0) {
      setSnackbar({
        open: true,
        message: "Please upload at least one product image",
        severity: "error",
      });
      return;
    }

    setIsLoading(true);

    try {
      const productRef = await projectFirestore.collection("products").add({
        name: newProduct.name,
        brand: newProduct.brand,
        model: newProduct.model,
        productType: isProductType,
        short_description: newProduct.shortDescription,
        price: newProduct.price,
        priceType: newProduct.priceType || "Fixed",
        discount: 0,
        productLocation: "",
        rating: "4.5",
        order: 0,
        isShippingAllow: true,
        external_url: "",
        description: "No description available.",
        features: "Add Features",
        status: "inactive",
        createdAt: timestamp.fromDate(new Date()),
        images: [],
        category: newProduct.category,
        subCategory: newProduct.subCategory,
        buyerType: newProduct.buyerType,
        showProductTo: newProduct.showProductTo,
        customProductUrl: newProduct.CustomProductUrl || "",
        newArrivals: false,
        popularProducts: false,
        topSellingProducts: false,
        isSubscription: isSubscription,
        productId: "",
        city: "",
        gst: "",
        cgst: "",
        sgst: "",
        cess: "",
      });

      const productId = productRef.id;

      const imageUrls = await Promise.all(
        imageFiles.map((file) => uploadImage(file, productId))
      );

      await productRef.update({ images: imageUrls });

      const categoryList = newProduct.category;
      const categoryPrefix =
        Array.isArray(categoryList) && categoryList.length > 0
          ? categoryList[0].substring(0, 3).toUpperCase()
          : "GEN";

      const now = new Date();
      const year = String(now.getFullYear()).slice(-2);
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      const countDocRef = projectFirestore
        .collection("settings")
        .doc("total_products_count");
      const countDocSnap = await countDocRef.get();

      let productCount;
      if (countDocSnap.exists) {
        const currentCount = countDocSnap.data().productsCount || 10000;
        productCount = currentCount + 1;
        await countDocRef.update({ productsCount: productCount });
      } else {
        productCount = 10001;
        await countDocRef.set({ productsCount: productCount });
      }

      const customProductId = `${categoryPrefix}-${year}${month}${day}-${productCount}`;

      await productRef.update({
        images: imageUrls,
        productId: customProductId,
      });

      setNewProduct({
        name: "",
        brand: "",
        model: "",
        hsn: "",
        sku: "",
        unitSold: "",
        shortDescription: "",
        price: "",
        priceType: "Fixed",
        category: [],
        subCategory: [],
        buyerType: "",
        showProductTo: [],
        images: [],
        CustomProductUrl: "",
      });
      setImageFiles([]);
      setImageUploadProgress(0);
      closeModal();
    } catch (error) {
      console.error("Error adding product:", error);
    } finally {
      setIsLoading(false);
      setIsSubscription(false);
      setProductType("general");
    }
  };

  const uploadImage = async (file, productId) => {
    const storageRef = projectStorage.ref(
      `product_img/${productId}/${file.name}`
    );
    const uploadTask = storageRef.put(file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setImageUploadProgress(progress);
        },
        (error) => reject(error),
        async () => {
          const url = await uploadTask.snapshot.ref.getDownloadURL();
          resolve(url);
        }
      );
    });
  };

  // Robust role check for modal rendering
  const canShowModal =
    isModalOpen &&
    user?.role &&
    ["admin", "superadmin"].includes(user.role.trim().toLowerCase());

  return (
    <div className="add-product-compo-parent-div">
      {canShowModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close-btn" onClick={closeModal}>
              &times;
            </button>
            <h2>Add New Product</h2>
            <form onSubmit={handleAddProductNew} className="add-product-form">
              {/* Buyer type */}
              <div className="product-buyer-type-container">
                <div>
                  <span>Select a Role </span>
                  <span>(Only selected role can see this product)</span>
                </div>

                <div className="product-buyer-type-options">
                  {["dealer", "retailer", "customer"].map((role) => (
                    <label
                      key={role}
                      className={`role-card ${
                        newProduct.buyerType === role ? "active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="buyerType"
                        value={role}
                        checked={newProduct.buyerType === role}
                        onChange={handleBuyerTypeChange}
                        required
                      />
                      <span className="role-name">
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="subscription-toggle-container">
                <label
                  className={`subscription-switch ${
                    isSubscription ? "active" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSubscription}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsSubscription(checked);
                      setProductType(checked ? "subscription" : "general");
                    }}
                  />
                  <span className="slider"></span>

                  <div className="toggle-text-with-info d-flex">
                    <span className="toggle-text">
                      {isSubscription
                        ? "Subscription Enabled"
                        : "Subscription Disabled"}
                    </span>
                    &nbsp;
                    {/* Info Icon with Tooltip */}
                    <div className="info-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="20px"
                        viewBox="0 -960 960 960"
                        width="20px"
                        fill="currentColor"
                      >
                        <path d="M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
                      </svg>

                      <div className="tooltip-box">
                        Enabling this option makes this product a
                        <strong> subscription-based product</strong>. Customers
                        can subscribe for repeated deliveries (weekly, monthly,
                        etc.) instead of one-time purchase.
                      </div>
                    </div>
                  </div>
                </label>
              </div>

              {/* Vendors */}
              {isMultiLocationEnabled && (
                <div className="product-vendor-select-container">
                  <div className="vendor-select-label">
                    <span>Select Vendors </span>
                    <span>(Only selected vendors can see this product)</span>
                  </div>

                  <Select
                    isMulti
                    options={getVendorOptions()}
                    value={
                      newProduct.showProductTo.includes("all")
                        ? [{ value: "all", label: "All Vendors" }]
                        : getVendorOptions().filter((option) =>
                            newProduct.showProductTo.includes(option.value)
                          )
                    }
                    onChange={handleVendorSelection}
                    className="vendor-multiselect"
                    classNamePrefix="vendor-select"
                    placeholder="Select vendors..."
                  />
                </div>
              )}

              {/* Images */}
              <div className="image-upload-container">
                {[0, 1, 2, 3, 4].map((index) => (
                  <div key={index} className="image-upload-box">
                    {imageFiles[index] ? (
                      <div className="image-preview">
                        <img
                          src={URL.createObjectURL(imageFiles[index])}
                          alt={`Preview ${index}`}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="remove-image-btn"
                        >
                          X
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id={`image-upload-${index}`}
                          accept="image/*"
                          onChange={(e) => handleImageChange(e, index)}
                          style={{ display: "none" }}
                        />
                        <label
                          htmlFor={`image-upload-${index}`}
                          className="upload-label"
                        >
                          +<span>Add Image</span>
                        </label>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Product details */}
              <input
                type="text"
                name="name"
                value={newProduct.name}
                onChange={handleInputChange}
                placeholder="Product Name"
                required
                className="form-input"
              />
              <input
                type="text"
                name="brand"
                value={newProduct.brand}
                onChange={handleInputChange}
                placeholder="Brand Name"
                required
                className="form-input"
              />
              <input
                type="text"
                name="model"
                value={newProduct.model}
                onChange={handleInputChange}
                placeholder="Model Name"
                required
                className="form-input"
              />
              <textarea
                name="shortDescription"
                value={newProduct.shortDescription}
                onChange={handleInputChange}
                placeholder="Short Description"
                className="form-input"
                rows="3"
              ></textarea>

              <div className="d-flex gap-2">
                <input
                  type="number"
                  name="price"
                  value={newProduct.price}
                  onChange={handleInputChange}
                  placeholder="Price"
                  required
                  className="form-input"
                />
                <select
                  name="priceType"
                  value={newProduct.priceType}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="Fixed">Fixed</option>
                  <option value="Onward">Onward</option>
                </select>
              </div>

              {/* Categories & Subcategories */}
              <div className="dropdown-row">
                <div className="form-input-container">
                  <label>Categories</label>
                  <Select
                    isMulti
                    options={getCategoryOptions()}
                    value={getCategoryOptions().filter((option) =>
                      newProduct.category.includes(option.value)
                    )}
                    onChange={handleCategorySelection}
                    className="category-multiselect"
                    classNamePrefix="category-select"
                    placeholder="Select categories..."
                    required
                  />
                </div>

                <div className="form-input-container">
                  <label>Subcategories</label>
                  <Select
                    isMulti
                    options={getSubCategoryOptions()}
                    value={getSubCategoryOptions().filter((option) =>
                      newProduct.subCategory.includes(option.value)
                    )}
                    onChange={handleSubCategorySelection}
                    className="subcategory-multiselect"
                    classNamePrefix="subcategory-select"
                    placeholder="Select subcategories..."
                    isDisabled={newProduct.category.length === 0}
                  />
                </div>
              </div>

              {/* Custom URL */}
              <input
                type="text"
                name="CustomProductUrl"
                value={newProduct.CustomProductUrl}
                onChange={handleInputChange}
                placeholder="Custom URL (Optional)"
                className="form-input"
              />

              {/* Buttons */}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={isLoading}>
                  {isLoading ? "Adding..." : "Save"}
                </button>
              </div>
            </form>
            {imageUploadProgress > 0 && (
              <p>Upload progress: {imageUploadProgress.toFixed(2)}%</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProducts;
